import React, { useState, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Card, CardTitle, Badge } from '../UI'
import styles from '../../_css/Simulation.module.css'
import { notifyError } from '../../utils/helper'
import { downloadPdf, fetchExportReportData } from '../../requests'
import { getAiSimulationDetails } from '../../requests'
import Empty from './Empty'
import { ANALYSIS_DONE_STATUSES, font } from './constants'
import { isSimulationLanguageEnglish } from './simulationTranslationStorage'
import useRole from '../../hooks/getRole'

const PER_PAGE = 5

async function readDownloadError(error) {
  const data = error?.response?.data
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text())
      return json?.error?.message || json?.message || 'Failed to download PDF'
    } catch {
      return 'Failed to download PDF'
    }
  }
  return data?.error?.message || data?.message || 'Failed to download PDF'
}

function formatExportDate(isoString) {
  if (!isoString) return '—'
  const d = new Date(isoString)
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
}

function DownloadTab({ recentSimulationId = null, recentSimulationStatus = null, projectId = null , recentSimulationDetails = null, selectedRun = null, onReturnToLatestRun = () => {}, translateLanguage = null, translationViewLang = 'or' }) {
  const context = useSelector((state) => state.context)
  const userInfo = useSelector((state) => state.user)
  const role = useRole()
  const location = useLocation()
  const navigate = useNavigate()
  const [isDownloading, setIsDownloading] = useState(false)
  const [translating, setTranslating] = useState(false)
  const [exportHistory, setExportHistory] = useState([])
  const [exportPagination, setExportPagination] = useState(null)
  const [exportLoading, setExportLoading] = useState(false)
  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(true)

  const showingEnglish =
    translationViewLang === 'en' ||
    new URLSearchParams(location.search).get('translate') === 'true'

  const loadExportHistory = useCallback(async (page = 1) => {
    setExportLoading(true)
    try {
      const res = await fetchExportReportData({ page, limit: PER_PAGE, simulation_id: recentSimulationId || details?.id })
      const payload = res?.data ?? res
      const list = Array.isArray(payload?.data) ? payload.data : []
      const pag = payload?.pagination ?? null
      setExportHistory(list)
      setExportPagination(pag)
    } catch (err) {
      notifyError(err?.response?.data?.message ?? 'Failed to load export history')
      setExportHistory([])
      setExportPagination(null)
    } finally {
      setExportLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    // A specific historical run was explicitly picked in the Runs tab — show it regardless of
    // the simulation's live status, instead of falling through to the "always latest" fetch below.
    if (selectedRun) {
      setDetails({ ...(recentSimulationDetails ?? {}), status: 6 })
      setLoading(false)
      return
    }

    if(recentSimulationDetails && (recentSimulationStatus === 6 || recentSimulationStatus === 91)){
      setDetails(recentSimulationDetails)
      setLoading(false)
      return
    }

    if (!recentSimulationId) {
      setDetails(null)
      setLoading(false)
      return
    }
    setLoading(true)
    getAiSimulationDetails(recentSimulationId)
      .then((res) => {setDetails(res?.data ?? null)})
      .catch(() => setDetails(null))
      .finally(() => setLoading(false))
  }, [recentSimulationId, recentSimulationDetails, selectedRun])

  useEffect(() => {
    loadExportHistory(1)
  }, [loadExportHistory])

  const goToExportPage = (page) => {
    if (page >= 1 && exportPagination && page <= exportPagination.totalPages) {
      loadExportHistory(page)
    }
  }

  if(!recentSimulationId){
    return (
      <Empty message="Failed simulation. Please create a new simulation again" />
    )
  }

  if(details?.status == -1) {
    return (
      <Empty message="Failed simulation. Please create a new simulation again" />
    )
  }
  if(details?.status == 1 || details?.status == 2 || details?.status == 3) {
    return (
      <Empty message="Queries generation in progress..." type="progress" />
    )
  }
  if(details?.status == 4){
    return (
      <Empty message="Queries generation completed. Please go to Queries tab and select the queries you want to run. Then click on the Run Simulation button to start the simulation." type="progress" />
    )
  }

  // A new run may be processing (status 5/90) or in some other transient state, but if an
  // earlier run already completed, keep letting the user download/browse it instead of
  // blocking the whole tab.
  const hasCompletedRun = Array.isArray(details?.aiSimulationRuns) && details.aiSimulationRuns.length > 0

  if((details?.status == 5 || details?.status == 90) && !hasCompletedRun){
    return (
      <Empty message="Report generation in progress..." type="progress" />
    )
  }

  if (!ANALYSIS_DONE_STATUSES.includes(Number(details?.status)) && !hasCompletedRun) {
    return (
     <Empty message="AI is still analyzing the data. Please wait for a moment." type="progress" />
    )
  }



  const handleTranslateLanguage = async () => {
    if (!translateLanguage || translating) return
    setTranslating(true)
    try {
      const lang = showingEnglish ? 'or' : 'en'
      const response = await translateLanguage(lang)
      if (!response) return
      const translate = lang === 'en' ? 'true' : 'false'
      const base = `/simulations/details/${recentSimulationId}/download?simulation_id=${recentSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}`
      if (role === 'super_admin') {
        navigate(`${base}&agency_id=${userInfo?.agencyId}&translate=${translate}`)
      } else {
        navigate(`${base}&translate=${translate}`)
      }
    } finally {
      setTranslating(false)
    }
  }

  const handleDownloadPdf = async () => {
    if (isDownloading) return
    setIsDownloading(true)
    try {
      const response = await downloadPdf(recentSimulationId, selectedRun?.id ?? null, showingEnglish);
      if (response?.status < 400 && response?.data instanceof Blob) {

        const blob = response.data;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `simulation-report-${recentSimulationId || 'report'}${selectedRun ? `-run-${selectedRun.id}` : ''}${showingEnglish ? '-en' : ''}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        await loadExportHistory(1)
      }
    } catch (error) {
      notifyError(await readDownloadError(error));
    } finally {
      setIsDownloading(false)
    }
  }

  const simLanguage = details?.language ?? recentSimulationDetails?.language
  const showTranslateButton =
    translateLanguage &&
    ANALYSIS_DONE_STATUSES.includes(Number(details?.status ?? recentSimulationStatus)) &&
    !isSimulationLanguageEnglish(simLanguage)

  return (
    <div className={styles.tabPage}>
      {showTranslateButton && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button
            type="button"
            onClick={handleTranslateLanguage}
            disabled={translating}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid rgba(0,0,0,0.12)',
              background: 'rgba(0,0,0,0.04)',
              color: '#363636',
              fontSize: 13,
              fontWeight: 600,
              cursor: translating ? 'not-allowed' : 'pointer',
              fontFamily: font,
              opacity: translating ? 0.85 : 1,
            }}
          >
            {translating ? 'Translating…' : showingEnglish ? 'Show original' : 'Translate to English'}
          </button>
        </div>
      )}
      {selectedRun && (
        <div
          onClick={onReturnToLatestRun}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#b45309',
            background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 999, padding: '4px 10px', marginBottom: 12, cursor: 'pointer',
          }}
        >
          ← Exporting Run #{selectedRun.id} — click to return to latest
        </div>
      )}
      <Card>
        <CardTitle sub="Export simulation results in various formats">Download Results</CardTitle>
        <div className={styles.downloadGrid}>
          {[
            { icon: '📄', label: 'PDF Report', desc: 'Full analysis report with persona profile, brand KPIs, competitive insights, and strategic recommendations.', format: 'PDF' },
          ].map((dl, i) => (
            <div key={i} className={styles.downloadCard}>
              <div className={styles.downloadCardHeader}>
                <div className={styles.downloadCardTitleRow}>
                  <span className={styles.downloadCardIcon}>{dl.icon}</span>
                  <div>
                    <div className={styles.downloadCardLabel}>{dl.label}</div>
                    <div className={styles.downloadCardMeta}>{dl.format}</div>
                  </div>
                </div>
                <Badge color="#22c55e">{dl.format}</Badge>
              </div>
              <p className={styles.downloadCardDesc}>{dl.desc}</p>
              <button
                type="button"
                className={styles.downloadCardBtn}
                onClick={handleDownloadPdf}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <>
                    <span className={styles.downloadBtnSpinner} aria-hidden />
                    Downloading
                  </>
                ) : (
                  <>📥 Download {dl.format}{showingEnglish ? ' (English)' : ''}{selectedRun ? ` (Run #${selectedRun.id})` : ''}</>
                )}
              </button>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <CardTitle sub="Previous exports from simulations">Export History</CardTitle>
        {exportLoading ? (
          <div className={styles.tabEmpty}>Loading export history…</div>
        ) : exportHistory.length === 0 ? (
          <div className={styles.tabEmpty}>No export history yet</div>
        ) : (
          <>
            {exportHistory.map((e) => (
              <div key={e.id} className={styles.downloadHistoryRow}>
                <div className={styles.downloadHistoryLeft}>
                  <span className={styles.downloadHistoryIcon}>📄</span>
                  <div>
                    <div className={styles.downloadHistoryFile}>{e.file_name}</div>
                    <div className={styles.downloadHistoryMeta}>
                      {formatExportDate(e.createdAt)}
                      {e.admin?.full_name && ` · by ${e.admin.full_name}`}
                      {e.ai_simulation?.project?.name && ` · ${e.ai_simulation.project.name}`}
                    </div>
                  </div>
                </div>
                <div className={styles.downloadHistoryRight}>
                  <Badge color="#22c55e">{(e.format || 'PDF').toUpperCase()}</Badge>
                  <span className={styles.downloadHistoryAction}></span>
                </div>
              </div>
            ))}
            {exportPagination && exportPagination.totalPages > 0 && (
              <div className={styles.pagination}>
                <span className={styles.paginationInfo}>
                  Showing {exportPagination.total === 0 ? 0 : (exportPagination.currentPage - 1) * (exportPagination.perPage || PER_PAGE) + 1}–{Math.min((exportPagination.currentPage) * (exportPagination.perPage || PER_PAGE), exportPagination.total)} of {exportPagination.total}
                </span>
                {exportPagination.totalPages > 0 && (
                  <div className={styles.paginationBtns}>
                    <button
                      type="button"
                      onClick={() => goToExportPage(exportPagination.prevPage ?? exportPagination.currentPage - 1)}
                      disabled={!exportPagination.hasPrevPage}
                      className={styles.pageBtn}
                      title="Previous page"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => goToExportPage(exportPagination.nextPage ?? exportPagination.currentPage + 1)}
                      disabled={!exportPagination.hasNextPage}
                      className={styles.pageBtn}
                      title="Next page"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  )
}

export default DownloadTab
